import type { SignalRow } from './api';

/**
 * A module page's line items in categories, the way the overview groups its
 * modules (comment #47). Kept in the app, keyed by the row labels each module
 * already publishes, so reports built before this get it too.
 *
 * A row no category claims lands in a final "More" group rather than
 * disappearing; a module whose rows all fall into one group shows them flat.
 */

type Rule = { title: string; test: (r: SignalRow) => boolean };
export type RowGroup = { title: string; rows: SignalRow[] };

const k = (re: RegExp) => (r: SignalRow) => re.test(r.k);
const t = (re: RegExp) => (r: SignalRow) => re.test(r.t);
const s = (re: RegExp) => (r: SignalRow) => re.test(r.s ?? '');

const RULES: Record<string, Rule[]> = {
  'property-profile': [
    { title: 'The home', test: k(/^(Bedrooms|Bathrooms|Car spaces|Dwelling type|Floor area|Build year)/) },
    { title: 'The land', test: k(/^(Land area|Frontage and depth|Primary zoning|Geography)/) },
    { title: 'Land value', test: k(/land value|Land value/i) },
    { title: 'Who lives around it', test: k(/within 200 m$/) },
    { title: 'History', test: k(/history/i) },
  ],
  'agent-intel': [
    { title: 'This campaign', test: k(/^(Asking price|Days on the market|Listing features)/) },
    { title: 'The agency', test: k(/^(Listing agency|How its guides|Underquote rate|What this guide|Agency quoting)/) },
    { title: 'Its recent sales', test: () => true },
  ],
  'buyer-fit': [
    { title: 'About you', test: k(/^(Buying as|Stage|Buying stage)/) },
  ],
  'street-forecast': [
    { title: 'Forecast', test: k(/-year forecast$|^Street forecast$/) },
    { title: 'The street', test: k(/^(Past growth|Sales on record|Dwellings on street|Renters on street|Median house rent)/) },
    { title: 'Against the suburb', test: k(/^Street vs suburb/) },
    { title: 'Recorded history', test: k(/^(Week of|\d{4}$)/) },
  ],
  'suburb-benchmark': [
    { title: 'Median prices', test: k(/median|Median/) },
    { title: 'Land', test: k(/^(Land share of price|Median land value)/) },
    { title: 'The suburb', test: () => true },
  ],
  'budget-exposure': [
    { title: 'As an investment', test: k(/^(Net cashflow|Gross yield|Rent estimate|Private rental share|Negatively-geared|Landlords making)/) },
    { title: 'Holding costs', test: k(/^(Purchase figure|Council rates|Mortgage stress|Lender fees|Tax treatment)/) },
  ],
  risks: [
    { title: 'Hazards and price growth', test: k(/— growth association$/) },
    {
      title: 'Across the suburb',
      test: k(/(share of the suburb|— suburb coverage|^Contamination points|^Erosion features|^EPA notices|exposure$|^Landlord insurance)/),
    },
    { title: 'At this address', test: () => true },
  ],
  'market-signals': [
    { title: 'Prices', test: k(/^(Median|12-month|Repeat sales|Land value|Auction)/) },
    { title: 'Who owns', test: k(/^(Tenure mix|Investor concentration|Growth in investor)/) },
    { title: 'Pressure', test: k(/^(Mortgage stress|Affordability)/) },
  ],
  'growth-drivers': [
    { title: 'Prices and growth', test: k(/(Price Growth|Median Sale Price|Sold at a Loss)/i) },
    { title: 'Supply and rent', test: k(/(Stock on Market|Months of Supply|Vacancy|Rent|Renters|Renter share)/i) },
    { title: 'The land', test: k(/(Acid sulfate|Flooding|Contamination)/i) },
    { title: 'The people', test: k(/(Overseas|Cultural|Public housing|Welfare)/i) },
  ],
  development: [
    { title: 'Approvals and supply', test: k(/^(Development applications|Council pipeline|New supply|Construction activity|Density)/) },
    { title: 'Zoning and rates', test: k(/^(Council rates|Primary zoning)/) },
    { title: 'Council projects', test: () => true },
  ],
  lifestyle: [
    { title: 'Livability scores', test: k(/^(Affluence|Lifestyle|Hip|Convenience|Family|Safety|Tranquility|Community)$/) },
    { title: 'Getting around', test: k(/^(Distance to the city|Bus|Train|Ferry)$/) },
    { title: 'Schools close by', test: k(/(School|College|Grammar)/) },
    { title: 'Nearby', test: () => true },
  ],
  demographics: [
    { title: 'Income', test: k(/income|Income/) },
    { title: 'Age', test: k(/^Aged /) },
    { title: 'Housing', test: k(/^(Owned outright|Mortgaged|Renting|Public housing) —/) },
    { title: 'Voting', test: k(/^(Voting lean|Labor|Greens|Liberal|One Nation|Other|Independent|Nationals)$/) },
    { title: 'Population', test: () => true },
  ],
  'rental-signals': [
    { title: 'This property', test: k(/^(Listed for rent|Rental listing history)/) },
    { title: 'This street', test: k(/on this street$/) },
    { title: 'The suburb', test: () => true },
  ],
  'properties-nearby': [
    { title: 'Sold', test: t(/SOLD/) },
    { title: 'For sale', test: () => true },
  ],
  schools: [
    { title: 'Primary', test: s(/primary/i) },
    { title: 'Secondary', test: s(/secondary/i) },
    { title: 'Combined', test: s(/combined/i) },
    { title: 'Catchments', test: () => true },
  ],
  'planning-zoning': [
    { title: 'This parcel', test: k(/^(Primary zoning code|Zone name|Development overlays)/) },
    { title: 'Building controls', test: k(/^(Most common height|Median floor-space)/) },
    { title: 'Map layers', test: () => true },
  ],
  crime: [
    { title: 'Overall', test: k(/^(Total crime|This property)/) },
    { title: 'By offence', test: () => true },
  ],
  'supply-demand': [
    { title: 'On the market', test: k(/^(Stock on market|Houses on market|Units on market|For sale within)/) },
    { title: 'Pace of sales', test: () => true },
  ],
  ethnicity: [
    { title: 'Parents’ birthplace', test: k(/parents? born/) },
    { title: 'Born overseas', test: k(/^(Residents born overseas|Born in )/) },
    { title: 'Languages', test: k(/^Speaks /) },
    { title: 'Integration', test: k(/^(Cultural integration|Capital growth association)/) },
    { title: 'Ancestry', test: () => true },
  ],
  'similar-suburbs': [
    { title: 'Comparable suburbs', test: (r) => r.v === 'Comparable' },
    { title: 'The area', test: () => true },
  ],
};

/** The offer window's rows run in method blocks, each headed by its method. */
function byMethod(rows: SignalRow[]): RowGroup[] {
  const groups: RowGroup[] = [];
  for (const r of rows) {
    const head = /^Method \d · (.+)$/.exec(r.k);
    if (head) groups.push({ title: head[1], rows: [r] });
    else if (/^The advertised price$/.test(r.k)) groups.push({ title: 'The asking price', rows: [r] });
    else if (groups.length) groups[groups.length - 1].rows.push(r);
    else groups.push({ title: 'The window', rows: [r] });
  }
  return groups;
}

export function groupRows(moduleKey: string, rows: SignalRow[]): RowGroup[] {
  if (moduleKey === 'offer-window') return byMethod(rows);
  const rules = RULES[moduleKey];
  if (!rules) return [{ title: '', rows }];
  const out = rules.map((rule) => ({ title: rule.title, rows: [] as SignalRow[] }));
  const more: SignalRow[] = [];
  for (const r of rows) {
    const i = rules.findIndex((rule) => rule.test(r));
    if (i >= 0) out[i].rows.push(r);
    else more.push(r);
  }
  // Buyer Fit's answers are everything that is not "about you": they lead.
  if (moduleKey === 'buyer-fit' && more.length) out.unshift({ title: 'Your answers', rows: more.splice(0) });
  const groups = [...out, ...(more.length ? [{ title: 'More', rows: more }] : [])].filter((g) => g.rows.length);
  return groups.length > 1 ? groups : [{ title: '', rows }];
}
